/**
 * Financial Statements Page
 *
 * Professional financial statement views with PDF export:
 * - Income Statement (Profit & Loss)
 * - Balance Sheet
 * - Cash Flow Statement
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Line, ComposedChart, Cell,
} from 'recharts';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import {
  exportIncomeStatementPDF,
  exportBalanceSheetPDF,
  exportCashFlowPDF,
  type IncomeStatementData,
  type BalanceSheetData,
  type CashFlowData,
} from '@/lib/pdf-export';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  Download,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Building2,
  Landmark,
  Wallet,
  ArrowDownRight,
  ArrowUpRight,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface BusinessProfile {
  id: string;
  name: string;
}

// ============================================
// Constants
// ============================================

const currentYear = new Date().getFullYear();

// ============================================
// Sub-Components
// ============================================

function StatementLineItem({
  label,
  amount,
  bold = false,
  indent = false,
  highlight = false,
  negative = false,
  borderTop = false,
  large = false,
}: {
  label: string;
  amount: number;
  bold?: boolean;
  indent?: boolean;
  highlight?: boolean;
  negative?: boolean;
  borderTop?: boolean;
  large?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between py-1.5 ${borderTop ? 'border-t-2 border-foreground pt-2 mt-1' : ''} ${highlight ? 'bg-muted/50 -mx-3 px-3 rounded' : ''}`}
    >
      <span className={`${indent ? 'pl-6' : ''} ${bold ? 'font-semibold' : ''} ${large ? 'text-base' : 'text-sm'}`}>
        {label}
      </span>
      <span
        className={`font-mono tabular-nums ${bold ? 'font-semibold' : ''} ${large ? 'text-base' : 'text-sm'} ${negative || amount < 0 ? 'text-destructive' : amount > 0 && highlight ? 'text-emerald-600' : ''}`}
      >
        {formatCurrency(amount)}
      </span>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-2 mt-4 mb-1 pb-1 border-b">
      <span className="text-sm font-bold uppercase tracking-wide text-muted-foreground">{title}</span>
    </div>
  );
}

// ============================================
// Income Statement Tab
// ============================================

function IncomeStatementTab({
  dateFrom,
  dateTo,
  businessProfileId,
  businessName,
}: {
  dateFrom: string;
  dateTo: string;
  businessProfileId: string;
  businessName?: string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['income-statement', dateFrom, dateTo, businessProfileId],
    queryFn: async () => {
      const result = await api.getIncomeStatement({
        dateFrom,
        dateTo,
        businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      });
      return result.data as IncomeStatementData;
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-muted-foreground">Loading income statement...</div>;
  }

  if (!data) {
    return <div className="text-center py-12 text-muted-foreground">No data available for the selected period.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Gross Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">{formatCurrency(data.revenue.gross)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Operating Expenses</CardTitle>
            <TrendingDown className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-destructive">{formatCurrency(data.operatingExpenses.total)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Net Income</CardTitle>
            {data.netIncome >= 0 ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-destructive" />}
          </CardHeader>
          <CardContent>
            <div className={`text-xl font-bold ${data.netIncome >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
              {formatCurrency(data.netIncome)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Profit Margin</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">
              {data.revenue.gross > 0 ? `${((data.netIncome / data.revenue.gross) * 100).toFixed(1)}%` : '—'}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Statement */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Income Statement</CardTitle>
            <Button variant="outline" size="sm" onClick={() => exportIncomeStatementPDF(data, businessName)}>
              <Download className="h-4 w-4 mr-1" />
              Export PDF
            </Button>
          </CardHeader>
          <CardContent>
            <SectionHeader title="Revenue" />
            {data.revenue.byType.map(r => (
              <StatementLineItem
                key={r.type}
                label={r.type.charAt(0).toUpperCase() + r.type.slice(1).replace('_', ' ') + ' Revenue'}
                amount={r.amount}
                indent
              />
            ))}
            <StatementLineItem label="Gross Revenue" amount={data.revenue.gross} bold />

            {data.costOfGoodsSold > 0 && (
              <>
                <SectionHeader title="Cost of Goods Sold" />
                <StatementLineItem label="Cost of Goods Sold" amount={data.costOfGoodsSold} indent />
                <StatementLineItem label="Gross Profit" amount={data.grossProfit} bold />
              </>
            )}

            <SectionHeader title="Operating Expenses" />
            {data.operatingExpenses.byCategory.map(e => (
              <StatementLineItem key={e.slug} label={e.category} amount={e.amount} indent />
            ))}
            {data.operatingExpenses.subscriptions.total > 0 && (
              <StatementLineItem
                label={`Subscriptions (${data.operatingExpenses.subscriptions.count})`}
                amount={data.operatingExpenses.subscriptions.total}
                indent
              />
            )}
            <StatementLineItem label="Total Operating Expenses" amount={data.operatingExpenses.total} bold />

            <StatementLineItem
              label="Net Income (Before Tax)"
              amount={data.netIncome}
              bold
              large
              borderTop
              highlight
            />
          </CardContent>
        </Card>

        {/* Monthly Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly Revenue vs Expenses</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={data.monthlyBreakdown}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="monthName" className="text-xs" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} className="text-xs" />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Legend />
                <Bar dataKey="revenue" name="Revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="netIncome" name="Net Income" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================
// Balance Sheet Tab
// ============================================

function BalanceSheetTab({
  asOfDate,
  businessProfileId,
  businessName,
}: {
  asOfDate: string;
  businessProfileId: string;
  businessName?: string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['balance-sheet', asOfDate, businessProfileId],
    queryFn: async () => {
      const result = await api.getBalanceSheet({
        asOfDate,
        businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      });
      return result.data as BalanceSheetData;
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-muted-foreground">Loading balance sheet...</div>;
  }

  if (!data) {
    return <div className="text-center py-12 text-muted-foreground">No data available.</div>;
  }

  const chartData = [
    { name: 'Current Assets', amount: data.assets.current.totalCurrent },
    { name: 'Fixed Assets', amount: data.assets.fixed.netFixedAssets },
    { name: 'Liabilities', amount: data.liabilities.totalLiabilities },
    { name: "Owner's Equity", amount: data.equity.ownersEquity },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Total Assets</CardTitle>
            <Building2 className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-blue-600">{formatCurrency(data.assets.totalAssets)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Total Liabilities</CardTitle>
            <Landmark className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-destructive">{formatCurrency(data.liabilities.totalLiabilities)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Owner's Equity</CardTitle>
            <Wallet className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className={`text-xl font-bold ${data.equity.ownersEquity >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
              {formatCurrency(data.equity.ownersEquity)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Debt-to-Equity</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">
              {data.equity.ownersEquity > 0
                ? `${(data.liabilities.totalLiabilities / data.equity.ownersEquity).toFixed(2)}x`
                : '—'}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Statement */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Balance Sheet</CardTitle>
            <Button variant="outline" size="sm" onClick={() => exportBalanceSheetPDF(data, businessName)}>
              <Download className="h-4 w-4 mr-1" />
              Export PDF
            </Button>
          </CardHeader>
          <CardContent>
            <SectionHeader title="Current Assets" />
            <StatementLineItem
              label={`Accounts Receivable (${data.assets.current.outstandingInvoiceCount} invoices)`}
              amount={data.assets.current.accountsReceivable}
              indent
            />
            <StatementLineItem label="Total Current Assets" amount={data.assets.current.totalCurrent} bold />

            <SectionHeader title="Fixed Assets" />
            {data.assets.fixed.byType.map(a => (
              <StatementLineItem
                key={a.type}
                label={`${a.type.charAt(0).toUpperCase() + a.type.slice(1)} (${a.count})`}
                amount={a.netBookValue}
                indent
              />
            ))}
            {data.assets.fixed.totalAccumulatedDepreciation > 0 && (
              <StatementLineItem
                label="Less: Accumulated Depreciation"
                amount={-data.assets.fixed.totalAccumulatedDepreciation}
                indent
                negative
              />
            )}
            <StatementLineItem label="Net Fixed Assets" amount={data.assets.fixed.netFixedAssets} bold />
            <StatementLineItem label="TOTAL ASSETS" amount={data.assets.totalAssets} bold large borderTop />

            <SectionHeader title="Liabilities" />
            {data.liabilities.byType.map(l => (
              <StatementLineItem
                key={l.type}
                label={`${l.type.charAt(0).toUpperCase() + l.type.slice(1)} (${l.count})`}
                amount={l.balance}
                indent
              />
            ))}
            {data.liabilities.byType.length === 0 && (
              <StatementLineItem label="No outstanding liabilities" amount={0} indent />
            )}
            <StatementLineItem label="Total Liabilities" amount={data.liabilities.totalLiabilities} bold />

            <SectionHeader title="Owner's Equity" />
            <StatementLineItem label="Owner's Equity" amount={data.equity.ownersEquity} indent />
            <StatementLineItem
              label="TOTAL LIABILITIES & EQUITY"
              amount={data.totalLiabilitiesAndEquity}
              bold
              large
              borderTop
            />
          </CardContent>
        </Card>

        {/* Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Asset & Liability Composition</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} className="text-xs" />
                <YAxis dataKey="name" type="category" width={120} className="text-xs" />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Bar dataKey="amount" name="Amount" radius={[0, 4, 4, 0]}>
                  {chartData.map((_entry, index) => {
                    const colors = ['#3b82f6', '#6366f1', '#ef4444', '#10b981'];
                    return <Cell key={index} fill={colors[index % colors.length]} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================
// Cash Flow Tab
// ============================================

function CashFlowTab({
  dateFrom,
  dateTo,
  businessProfileId,
  businessName,
}: {
  dateFrom: string;
  dateTo: string;
  businessProfileId: string;
  businessName?: string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['cash-flow', dateFrom, dateTo, businessProfileId],
    queryFn: async () => {
      const result = await api.getCashFlowStatement({
        dateFrom,
        dateTo,
        businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      });
      return result.data as CashFlowData;
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-muted-foreground">Loading cash flow statement...</div>;
  }

  if (!data) {
    return <div className="text-center py-12 text-muted-foreground">No data available for the selected period.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Cash In</CardTitle>
            <ArrowDownRight className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-emerald-600">{formatCurrency(data.operating.cashReceived)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Cash Out</CardTitle>
            <ArrowUpRight className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-destructive">
              {formatCurrency(data.operating.expensesPaid + data.operating.taxPayments + data.investing.totalPurchases + data.financing.loanPayments)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Net Cash Flow</CardTitle>
            {data.netCashFlow >= 0 ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-destructive" />}
          </CardHeader>
          <CardContent>
            <div className={`text-xl font-bold ${data.netCashFlow >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
              {formatCurrency(data.netCashFlow)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Operating Ratio</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">
              {data.operating.cashReceived > 0
                ? `${((data.operating.netOperating / data.operating.cashReceived) * 100).toFixed(1)}%`
                : '—'}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Statement */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Statement of Cash Flows</CardTitle>
            <Button variant="outline" size="sm" onClick={() => exportCashFlowPDF(data, businessName)}>
              <Download className="h-4 w-4 mr-1" />
              Export PDF
            </Button>
          </CardHeader>
          <CardContent>
            <SectionHeader title="Operating Activities" />
            <StatementLineItem
              label={`Cash from Customers (${data.operating.paymentCount} payments)`}
              amount={data.operating.cashReceived}
              indent
            />
            <StatementLineItem
              label={`Expenses Paid (${data.operating.expenseCount})`}
              amount={-data.operating.expensesPaid}
              indent
              negative
            />
            <StatementLineItem label="Tax Payments" amount={-data.operating.taxPayments} indent negative />
            <StatementLineItem label="Net Cash from Operations" amount={data.operating.netOperating} bold highlight />

            <SectionHeader title="Investing Activities" />
            {data.investing.purchases.map(p => (
              <StatementLineItem
                key={p.type}
                label={`${p.type.charAt(0).toUpperCase() + p.type.slice(1)} Purchases (${p.count})`}
                amount={-p.amount}
                indent
                negative
              />
            ))}
            {data.investing.disposalProceeds > 0 && (
              <StatementLineItem label="Asset Disposal Proceeds" amount={data.investing.disposalProceeds} indent />
            )}
            {data.investing.purchases.length === 0 && data.investing.disposalProceeds === 0 && (
              <StatementLineItem label="No investing activity" amount={0} indent />
            )}
            <StatementLineItem label="Net Cash from Investing" amount={data.investing.netInvesting} bold highlight />

            <SectionHeader title="Financing Activities" />
            <StatementLineItem label="Loan Interest Payments" amount={-data.financing.loanPayments} indent negative />
            <StatementLineItem label="Net Cash from Financing" amount={data.financing.netFinancing} bold highlight />

            <StatementLineItem
              label="Net Change in Cash"
              amount={data.netCashFlow}
              bold
              large
              borderTop
              highlight
            />
          </CardContent>
        </Card>

        {/* Monthly Cash Flow Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly Cash Flow</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={data.monthlyBreakdown}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="monthName" className="text-xs" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} className="text-xs" />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Legend />
                <Bar dataKey="cashIn" name="Cash In" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="cashOut" name="Cash Out" fill="#ef4444" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="netFlow" name="Net Flow" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================
// Main Page Component
// ============================================

export function FinancialStatementsPage() {
  const [activeTab, setActiveTab] = useState('income');
  const [businessProfileId, setBusinessProfileId] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState(`${currentYear}-01-01`);
  const [dateTo, setDateTo] = useState(`${currentYear}-12-31`);

  // Load business profiles
  const { data: profilesData } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: () => api.getBusinessProfiles(),
  });
  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const selectedProfile = profiles.find(p => p.id === businessProfileId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Financial Statements</h1>
          <p className="text-muted-foreground">Generate professional statements with PDF export</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={businessProfileId} onValueChange={setBusinessProfileId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Businesses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Businesses</SelectItem>
              {profiles.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">From</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-[150px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="w-[150px]"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="income" className="gap-1">
            <FileText className="h-4 w-4" />
            Income Statement
          </TabsTrigger>
          <TabsTrigger value="balance" className="gap-1">
            <Building2 className="h-4 w-4" />
            Balance Sheet
          </TabsTrigger>
          <TabsTrigger value="cashflow" className="gap-1">
            <DollarSign className="h-4 w-4" />
            Cash Flow
          </TabsTrigger>
        </TabsList>

        <TabsContent value="income">
          <IncomeStatementTab
            dateFrom={dateFrom}
            dateTo={dateTo}
            businessProfileId={businessProfileId}
            businessName={selectedProfile?.name}
          />
        </TabsContent>

        <TabsContent value="balance">
          <BalanceSheetTab
            asOfDate={dateTo}
            businessProfileId={businessProfileId}
            businessName={selectedProfile?.name}
          />
        </TabsContent>

        <TabsContent value="cashflow">
          <CashFlowTab
            dateFrom={dateFrom}
            dateTo={dateTo}
            businessProfileId={businessProfileId}
            businessName={selectedProfile?.name}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
